/**
 * Context-Free Grammar (CFG) for procedural room description generation
 * 
 * Based on Fitch & Friederici (2012) and ShaggyDev (2022) on generative grammars.
 * 
 * A CFG is a formal grammar consisting of production rules that generate strings
 * in a formal language. In this game, it creates unique, natural-sounding room
 * descriptions by recursively expanding non-terminal symbols.
 * 
 * Educational Purpose:
 * - Demonstrates formal language theory in practice
 * - Shows how grammars can generate infinite variations from finite rules
 * - Illustrates the power of recursive production rules
 * 
 * Grammar Structure:
 * S -> "You are in a {adjective} {place}."
 * adjective -> "dimly lit" | "mysterious" | "ancient" | ...
 * place -> "chamber" | "corridor" | "laboratory" | ...
 */

export class CFG {
  private rules: Record<string, string[]>;

  /**
   * Initialize the CFG with default production rules
   * Rules can be extended or modified for different narrative styles
   */
  constructor() {
    this.rules = {
      // Start symbol - the root of all descriptions
      S: ['You are in a {adjective} {place}.'],
      
      // Adjectives to describe the atmosphere
      adjective: [
        'dimly lit',
        'mysterious',
        'ancient',
        'spacious',
        'eerie',
        'vast',
        'cramped',
        'ornate',
        'dusty',
        'cold',
      ],
      
      // Types of rooms/locations
      place: [
        'chamber',
        'corridor',
        'laboratory',
        'hall',
        'vault',
        'passage',
        'gallery',
        'sanctum',
        'alcove',
        'antechamber',
      ],
    };
  }

  /**
   * Generate a string by recursively expanding a symbol
   * 
   * This is the core of the CFG - it takes a symbol and:
   * 1. If it's a terminal (no rules), return it as-is
   * 2. If it's a non-terminal, randomly choose a production
   * 3. Recursively expand any non-terminals in the production
   * 
   * @param symbol - The symbol to expand (default: 'S' for start)
   * @returns A fully expanded string with all non-terminals replaced
   */
  generate(symbol: string = 'S'): string {
    // Base case: if no rule exists for this symbol, it's a terminal
    if (!this.rules[symbol]) {
      return symbol;
    }

    // Choose a random production for this symbol
    const production = this.randomChoice(this.rules[symbol]);

    // Recursively expand all non-terminals in the production
    // Non-terminals are marked with curly braces: {symbol}
    const result = production.replace(/{(.*?)}/g, (_, nonterm) => {
      return this.generate(nonterm);
    });

    return result;
  }

  /**
   * Add a new production rule or extend an existing one
   * This allows for dynamic grammar expansion
   * 
   * @param symbol - The non-terminal symbol
   * @param productions - Array of production strings
   */
  addRule(symbol: string, productions: string[]): void {
    this.rules[symbol] = productions;
  }

  /**
   * Extend an existing rule with additional productions
   * 
   * @param symbol - The non-terminal symbol to extend
   * @param productions - Additional productions to add
   */
  extendRule(symbol: string, productions: string[]): void {
    if (this.rules[symbol]) {
      this.rules[symbol].push(...productions);
    } else {
      this.addRule(symbol, productions);
    }
  }

  /**
   * Get all production rules for a symbol
   * 
   * @param symbol - The non-terminal symbol
   * @returns Array of production strings, or undefined if symbol doesn't exist
   */
  getRule(symbol: string): string[] | undefined {
    return this.rules[symbol];
  }

  /**
   * Get all defined non-terminal symbols
   * Useful for debugging and educational display
   * 
   * @returns Array of all non-terminal symbols
   */
  getSymbols(): string[] {
    return Object.keys(this.rules);
  }

  /**
   * Randomly select an element from an array
   * Uses cryptographically weak randomness (Math.random) which is
   * sufficient for game purposes
   * 
   * @param arr - Array to choose from
   * @returns A random element from the array
   */
  private randomChoice<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)]!;
  }

  /**
   * Generate multiple unique descriptions
   * Attempts to generate unique strings, but duplicates are possible
   * with small grammars
   * 
   * @param count - Number of descriptions to generate
   * @param symbol - Starting symbol (default: 'S')
   * @returns Array of generated descriptions
   */
  generateMultiple(count: number, symbol: string = 'S'): string[] {
    const descriptions: string[] = [];
    for (let i = 0; i < count; i++) {
      descriptions.push(this.generate(symbol));
    }
    return descriptions;
  }

  /**
   * Reset the grammar to default rules
   * Useful for testing or returning to original state
   */
  reset(): void {
    this.rules = {
      S: ['You are in a {adjective} {place}.'],
      adjective: [
        'dimly lit',
        'mysterious',
        'ancient',
        'spacious',
        'eerie',
        'vast',
      ],
      place: ['chamber', 'corridor', 'laboratory', 'hall', 'vault', 'passage'],
    };
  }
}